import type { ExtractedDocument, ExtractedDocumentBlock, ExtractedTextSelection } from '../documentExtraction';
import { sourceRectanglesForSelection, sourceReferencesForSelection } from '../documentExtraction';

export interface OntologySelection extends ExtractedTextSelection {
    blockRanges: Array<{ blockId: string; end: number; start: number }>;
    toolbar: { left: number; top: number };
}

const getBlockElement = (node: Node | null) => {
    const element = node instanceof HTMLElement ? node : node?.parentElement;

    return element?.closest<HTMLElement>('[data-ontology-block-id]');
};

const getOffsetWithin = (element: HTMLElement, node: Node, offset: number) => {
    const range = document.createRange();

    range.selectNodeContents(element);
    range.setEnd(node, offset);
    return range.toString().length;
};

const getBlockRanges = (
    blocks: ExtractedDocumentBlock[],
    startBlockId: string,
    startOffset: number,
    endBlockId: string,
    endOffset: number,
) => {
    const startIndex = blocks.findIndex((block) => block.id === startBlockId);
    const endIndex = blocks.findIndex((block) => block.id === endBlockId);

    if (startIndex < 0 || endIndex < startIndex) {
        return [];
    }

    return blocks.slice(startIndex, endIndex + 1).map((block, index, selectedBlocks) => ({
        blockId: block.id,
        end: index === selectedBlocks.length - 1 ? Math.min(endOffset, block.text.length) : block.text.length,
        start: index === 0 ? Math.min(startOffset, block.text.length) : 0,
    }));
};

export const createOntologySelection = (
    root: HTMLElement,
    extractedDocument: ExtractedDocument,
): OntologySelection | undefined => {
    const browserSelection = window.getSelection();

    if (!browserSelection?.rangeCount || browserSelection.isCollapsed) {
        return undefined;
    }

    const range = browserSelection.getRangeAt(0);
    const startElement = getBlockElement(range.startContainer);
    const endElement = getBlockElement(range.endContainer);

    if (!startElement || !endElement || !root.contains(startElement) || !root.contains(endElement)) {
        return undefined;
    }

    const startBlockId = startElement.dataset.ontologyBlockId;
    const endBlockId = endElement.dataset.ontologyBlockId;

    if (!startBlockId || !endBlockId) {
        return undefined;
    }

    const startOffset = getOffsetWithin(startElement, range.startContainer, range.startOffset);
    const endOffset = getOffsetWithin(endElement, range.endContainer, range.endOffset);
    const blockRanges = getBlockRanges(
        extractedDocument.blocks,
        startBlockId,
        startOffset,
        endBlockId,
        endOffset,
    ).filter((entry) => entry.end > entry.start);

    if (!blockRanges.length) {
        return undefined;
    }

    const firstRange = blockRanges[0];
    const lastRange = blockRanges.at(-1) ?? firstRange;
    const firstBlock = extractedDocument.blocks.find((block) => block.id === firstRange.blockId);
    const lastBlock = extractedDocument.blocks.find((block) => block.id === lastRange.blockId);

    if (!firstBlock || !lastBlock) {
        return undefined;
    }

    const quote = browserSelection.toString().replace(/\s+/g, ' ').trim();
    const bounds = range.getBoundingClientRect();
    const sourceRectangles = blockRanges.flatMap((selectedRange) => {
        const block = extractedDocument.blocks.find((candidate) => candidate.id === selectedRange.blockId);

        return block ? sourceRectanglesForSelection(block, selectedRange.start, selectedRange.end) : [];
    });
    const sourceReferences = blockRanges.flatMap((selectedRange) => {
        const block = extractedDocument.blocks.find((candidate) => candidate.id === selectedRange.blockId);

        return block ? sourceReferencesForSelection(block, selectedRange.start, selectedRange.end) : [];
    });

    return {
        blockRanges,
        contextAfter: lastBlock.text.slice(lastRange.end, lastRange.end + 120),
        contextBefore: firstBlock.text.slice(Math.max(0, firstRange.start - 120), firstRange.start),
        end: { blockId: lastRange.blockId, offset: lastRange.end },
        quote,
        sourceRectangles,
        sourceReferences,
        start: { blockId: firstRange.blockId, offset: firstRange.start },
        toolbar: {
            left: Math.min(Math.max(bounds.left, 8), Math.max(window.innerWidth - 360, 8)),
            top: Math.max(bounds.top - 48, 8),
        },
    };
};
